/**
 * Seniors Overview Grid
 * 
 * Displays a grid of senior cards with photos, status, reminders, and quick actions
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SeniorAvatar from '../../SeniorAvatar';
import axiosClient from '../../../api/axiosClient';
import { normalizeDeviceStatus } from '../../../utils/deviceStatus';

function SeniorsOverviewGrid({ seniors, devices, household, currentCall }) {
  const navigate = useNavigate();
  const [seniorsWithReminders, setSeniorsWithReminders] = useState({});
  const [loadingReminders, setLoadingReminders] = useState(true);

  // Load next reminders for each senior
  useEffect(() => {
    const loadReminders = async () => {
      if (!seniors || seniors.length === 0) {
        setLoadingReminders(false);
        return;
      }

      setLoadingReminders(true);
      const remindersMap = {};

      try {
        await Promise.all(
          seniors.map(async (senior) => {
            try {
              const response = await axiosClient.get(`/api/family/seniors/${senior.id}/reminders`);
              const reminders = response.data || [];
              
              // Filter upcoming reminders
              const upcoming = reminders
                .filter(r => r.status === 'pending')
                .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
                .slice(0, 2);
              
              remindersMap[senior.id] = upcoming;
            } catch (error) {
              console.error(`Failed to load reminders for senior ${senior.id}:`, error);
              remindersMap[senior.id] = [];
            }
          })
        );

        setSeniorsWithReminders(remindersMap);
      } catch (error) {
        console.error('Failed to load reminders:', error);
      } finally {
        setLoadingReminders(false);
      }
    };

    loadReminders();
  }, [seniors]);

  const getSeniorDevices = (seniorId) => {
    return devices?.filter(d => d.senior_id === seniorId) || [];
  };

  const getDeviceStatus = (seniorId) => {
    const seniorDevices = getSeniorDevices(seniorId);
    
    if (!seniorDevices || seniorDevices.length === 0) {
      return { text: 'Kein Gerät', color: 'text-neutral-500', bg: 'bg-neutral-500/20', icon: '📱' };
    }
    
    // Check if in active call
    const inCall = currentCall && 
                   (currentCall.status === 'ringing' || currentCall.status === 'in_call') &&
                   seniorDevices.some(d => d.id === currentCall.deviceId);
    
    // Use normalizeDeviceStatus for each device and pick the highest priority status
    const deviceStatuses = seniorDevices.map(d => normalizeDeviceStatus(d, inCall));
    
    // Sort by priority (lower number = higher priority)
    deviceStatuses.sort((a, b) => a.priority - b.priority);
    
    // Return the highest priority status
    const topStatus = deviceStatuses[0];
    
    // Map to old format for backwards compatibility
    return {
      text: topStatus.label,
      color: topStatus.statusType === 'online' ? 'text-green-400' :
             topStatus.statusType === 'offline' ? 'text-neutral-400' :
             topStatus.statusType === 'call' ? 'text-cyan-400' :
             topStatus.statusType === 'inconsistent' ? 'text-orange-400' :
             'text-yellow-400',
      bg: topStatus.statusType === 'online' ? 'bg-green-500/20' :
          topStatus.statusType === 'offline' ? 'bg-neutral-500/20' :
          topStatus.statusType === 'call' ? 'bg-cyan-500/20' :
          topStatus.statusType === 'inconsistent' ? 'bg-orange-500/20' :
          'bg-yellow-500/20',
      icon: topStatus.emoji,
      pulse: topStatus.statusType === 'call'
    };
  };

  const formatReminderTime = (scheduledTime) => {
    const date = new Date(scheduledTime);
    const now = new Date();
    const diff = date - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `in ${days} Tag${days > 1 ? 'en' : ''}`;
    if (hours > 0) return `in ${hours} Stunde${hours > 1 ? 'n' : ''}`;
    return 'Bald';
  };

  const handleCallClick = (senior) => {
    // TODO: Implement direct call functionality
    navigate(`/family/seniors/${senior.id}`);
  };

  if (!seniors || seniors.length === 0) {
    return (
      <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-700/50 p-12 text-center">
        <div className="text-6xl mb-4">👴</div>
        <h3 className="text-xl font-bold text-white mb-2">Noch keine Senioren hinzugefügt</h3>
        <p className="text-neutral-400 mb-6">Füge deinen ersten Senior hinzu, um loszulegen</p>
        <button
          onClick={() => navigate('/family/seniors')}
          className="px-6 py-3 bg-gradient-to-r from-[#6DCEE8] to-[#5B4B8A] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6DCEE8]/50 transition-all"
        >
          Senior hinzufügen
        </button>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {seniors.map((senior, idx) => {
        const status = getDeviceStatus(senior.id);
        const reminders = seniorsWithReminders[senior.id] || [];
        
        return (
          <div
            key={senior.id}
            className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-700/50 p-6 hover:shadow-2xl hover:shadow-[#6DCEE8]/20 hover:scale-[1.02] transition-all duration-300 animate-scale-in"
            style={{ animationDelay: `${idx * 100}ms` }}
          >
            {/* Avatar & Name */}
            <div className="flex items-start gap-4 mb-4">
              <SeniorAvatar senior={senior} size="lg" />
              
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white truncate mb-1">{senior.name}</h3>
                {senior.language && (
                  <p className="text-sm text-neutral-400">Sprache: {senior.language}</p>
                )}
                
                {/* Status Badge */}
                <div className="mt-2">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color} ${status.pulse ? 'animate-pulse' : ''}`}>
                    <span>{status.icon}</span>
                    {status.text}
                  </span>
                </div>
              </div>
            </div>

            {/* Reminders Section */}
            <div className="bg-neutral-800/50 rounded-xl p-4 mb-4 min-h-[80px]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">⏰</span>
                <span className="text-sm font-semibold text-neutral-300">Nächste Erinnerungen</span>
              </div>
              
              {loadingReminders ? (
                <div className="space-y-2">
                  <div className="h-4 bg-neutral-700 rounded animate-pulse"></div>
                  <div className="h-4 bg-neutral-700 rounded animate-pulse w-3/4"></div>
                </div>
              ) : reminders.length > 0 ? (
                <div className="space-y-2">
                  {reminders.map((reminder, ridx) => (
                    <div key={ridx} className="flex items-center justify-between text-sm">
                      <span className="text-neutral-300 truncate flex-1 mr-2">{reminder.title}</span>
                      <span className="text-[#F6B352] font-medium whitespace-nowrap">
                        {formatReminderTime(reminder.scheduled_time)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500">Keine Erinnerungen geplant</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/family/seniors/${senior.id}`)}
                className="flex-1 px-4 py-2 bg-neutral-700 text-white rounded-xl hover:bg-neutral-600 transition-colors font-medium"
              >
                Details
              </button>
              <button
                onClick={() => handleCallClick(senior)}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-[#6DCEE8] to-[#5B4B8A] text-white rounded-xl hover:shadow-lg hover:shadow-[#6DCEE8]/50 transition-all font-medium"
              >
                📞 Anruf
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default SeniorsOverviewGrid;
